import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Briefcase, Plus, X, Send, ShieldCheck, Trophy, Lock, ArrowRight } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import MarketTabs from "@/components/MarketTabs";
import SiteFooter from "@/components/SiteFooter";
import Seo from "@/components/Seo";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import { ENGINES } from "@/content/engines";

const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-xs text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue";
const labelCls = "font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5";

function EngineChip({ engine, gameTitle }) {
  const eng = ENGINES[engine] || { short: engine?.toUpperCase(), accent: "text-zinc-400 border-white/15" };
  const Icon = eng.icon;
  return (
    <span className={`font-mono text-[8px] tracking-[0.2em] uppercase border px-2 py-0.5 flex items-center gap-1.5 ${eng.accent}`}>
      {Icon && <Icon className="w-3 h-3" />} {gameTitle || eng.short}
    </span>
  );
}

export default function JobsPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { fmt } = useCurrency();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState(params.get("for") === "contractors" ? "browse" : "browse");
  const [jobs, setJobs] = useState([]);
  const [mine, setMine] = useState([]);
  const [engine, setEngine] = useState("");
  const [detail, setDetail] = useState(null);
  const [pitch, setPitch] = useState({ pitch: "", rate: "" });
  const [form, setForm] = useState({ title: "", description: "", engine: "ue5", game_title: "", budget: "", skills: "", deadline: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/jobs", { params: engine ? { engine } : {} }).then((r) => setJobs(r.data)).catch(() => {});
    api.get("/auth/me").then((r) => {
      setMe(r.data);
      api.get("/jobs/mine").then((m) => setMine(m.data)).catch(() => {});
    }).catch(() => setMe(null));
  }, [engine]);
  useEffect(() => { load(); }, [load]);

  const isDev = me && (me.role === "dev" || me.role === "admin");
  const canPitch = isDev;

  const submitJob = async (e) => {
    e.preventDefault();
    if (!me) { toast.error("Sign in to post a job"); navigate("/auth"); return; }
    setBusy(true);
    try {
      await api.post("/jobs", {
        title: form.title, description: form.description, engine: form.engine,
        game_title: form.game_title, budget: parseFloat(form.budget),
        skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
        deadline: form.deadline,
      });
      toast.success("Job posted — verified contractors can now pitch");
      setForm({ title: "", description: "", engine: "ue5", game_title: "", budget: "", skills: "", deadline: "" });
      setTab("mine");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const submitPitch = async () => {
    setBusy(true);
    try {
      await api.post(`/jobs/${detail.id}/pitch`, { pitch: pitch.pitch, rate: parseFloat(pitch.rate) });
      toast.success("Pitch submitted — the client will review it");
      setDetail(null); setPitch({ pitch: "", rate: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const award = async (job, applicant) => {
    setBusy(true);
    try {
      const r = await api.post(`/jobs/${job.id}/award`, { dev_id: applicant.dev_id });
      toast.success(r.data.message);
      load();
      navigate(`/command?engagement=${r.data.engagement_id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const closeJob = async (job) => {
    try {
      await api.post(`/jobs/${job.id}/close`);
      toast.success("Job closed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const TABS = [["browse", "Browse Jobs"], ["post", "Post a Job"], ...(me ? [["mine", `My Jobs (${mine.length})`]] : [])];

  return (
    <div data-testid="jobs-page" className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col selection:bg-nc-blue/30">
      <Seo
        title="Job Market — Post Game Dev Jobs, Pitch Contracts"
        description="Post game development jobs and receive pitches from identity-verified contractors — or browse open jobs and pitch your services. Escrow-protected engagements across UE5, FiveM (GTA V), Arma Reforger, Minecraft and custom games."
        path="/jobs"
        keywords="game dev jobs, hire game developers, freelance game jobs, fivem jobs, unreal engine freelance, game contractor marketplace"
      />
      <SiteNav />
      <MarketTabs />

      <header className="border-b border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 grid-texture opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-14">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p className="font-mono text-xs tracking-[0.4em] text-nc-orange uppercase mb-4">Contract Network // Job Market</p>
            <h1 data-testid="jobs-title" className="font-sans font-bold uppercase tracking-tight text-3xl sm:text-5xl text-zinc-50">
              Post work. <span className="text-nc-blue">Pitch contracts.</span>
            </h1>
            <p className="font-mono text-sm text-zinc-400 mt-5 max-w-2xl leading-relaxed">
              Clients post jobs and receive pitches from identity-verified, clearance-rated contractors.
              Awarding a pitch creates an escrow-protected engagement automatically — funds held by Stripe until the work is approved.
            </p>
            {params.get("for") === "contractors" && !isDev && (
              <div data-testid="contractor-banner" className="mt-6 border border-nc-blue/40 bg-nc-blue/5 px-5 py-4 max-w-2xl flex items-center gap-4 flex-wrap">
                <p className="font-mono text-[11px] text-zinc-300 flex-1">Want to pitch these jobs? Enlist as a contractor, complete verification, and the market is yours.</p>
                <Link to="/auth" data-testid="contractor-enlist-cta" className="font-mono text-[10px] tracking-[0.2em] uppercase bg-nc-blue text-white px-4 py-2.5 hover:bg-nc-blue/80 transition-colors">Enlist Now</Link>
              </div>
            )}
          </motion.div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10 w-full flex flex-col gap-6">
        <div className="flex items-center gap-2 border-b border-white/10">
          {TABS.map(([id, label]) => (
            <button
              key={id}
              data-testid={`jobs-tab-${id}`}
              onClick={() => setTab(id)}
              className={`font-mono text-[10px] tracking-[0.2em] uppercase px-4 py-2.5 border-b-2 transition-colors ${tab === id ? "border-nc-orange text-nc-orange" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "browse" && (
          <>
            <div className="flex flex-wrap gap-2">
              <button data-testid="jobs-filter-all" onClick={() => setEngine("")} className={`font-mono text-[9px] tracking-[0.2em] uppercase border px-3 py-1.5 transition-colors ${engine === "" ? "text-nc-blue border-nc-blue/60 bg-nc-blue/10" : "text-zinc-400 border-white/10 hover:border-white/30"}`}>All</button>
              {Object.keys(ENGINES).map((k) => (
                <button key={k} data-testid={`jobs-filter-${k}`} onClick={() => setEngine(engine === k ? "" : k)} className={`font-mono text-[9px] tracking-[0.2em] uppercase border px-3 py-1.5 transition-colors ${engine === k ? "text-nc-blue border-nc-blue/60 bg-nc-blue/10" : "text-zinc-400 border-white/10 hover:border-white/30"}`}>{ENGINES[k].short}</button>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {jobs.length === 0 && <p data-testid="jobs-empty" className="font-mono text-[10px] text-zinc-600 py-10 col-span-full text-center">No open jobs right now — post the first one.</p>}
              {jobs.map((j, i) => (
                <button key={j.id} data-testid={`job-card-${i + 1}`} onClick={() => setDetail(j)} className="text-left border border-white/10 bg-black/40 hover:border-nc-blue/50 transition-colors duration-300 flex flex-col">
                  <div className="border-b border-white/10 px-4 py-2 flex items-center justify-between gap-2">
                    <EngineChip engine={j.engine} gameTitle={j.game_title} />
                    <span className="font-mono text-[8px] text-zinc-600 uppercase tracking-[0.2em]">{j.applicant_count} {j.applicant_count === 1 ? "pitch" : "pitches"}</span>
                  </div>
                  <div className="p-4 flex flex-col gap-2 flex-1">
                    <p className="font-sans font-bold uppercase text-sm text-zinc-100 leading-tight">{j.title}</p>
                    <p className="font-mono text-[10px] text-zinc-500 line-clamp-2 leading-relaxed">{j.description}</p>
                    <div className="flex flex-wrap gap-1.5 mt-auto pt-2">
                      {j.skills.slice(0, 4).map((s) => <span key={s} className="font-mono text-[8px] text-zinc-500 border border-white/10 px-1.5 py-0.5">{s}</span>)}
                    </div>
                  </div>
                  <div className="border-t border-white/10 px-4 py-2.5 flex items-center justify-between">
                    <span className="font-sans font-bold text-base text-nc-green">{fmt(j.budget, 0)}</span>
                    <span className="font-mono text-[8px] text-zinc-600 uppercase">{String(j.created_at).slice(0, 10)}</span>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

        {tab === "post" && (
          <form data-testid="job-post-form" onSubmit={submitJob} className="border border-white/10 bg-black/40 p-6 max-w-3xl flex flex-col gap-5">
            <p className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-nc-green" /> Every awarded job becomes an escrow-protected engagement automatically</p>
            <div>
              <label className={labelCls}>Job title *</label>
              <input data-testid="job-title" required minLength={5} className={inputCls} value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. Custom inventory system for FiveM RP server" />
            </div>
            <div>
              <label className={labelCls}>Engine / platform *</label>
              <div className="flex flex-wrap gap-2">
                {Object.keys(ENGINES).map((k) => (
                  <button type="button" key={k} data-testid={`job-engine-${k}`} onClick={() => setForm((f) => ({ ...f, engine: k }))} className={`font-mono text-[9px] tracking-[0.15em] uppercase border px-3 py-1.5 transition-colors ${form.engine === k ? "text-nc-blue border-nc-blue/60 bg-nc-blue/10" : "text-zinc-400 border-white/10 hover:border-white/30"}`}>{ENGINES[k].short}</button>
                ))}
              </div>
            </div>
            {form.engine === "custom" && (
              <div>
                <label className={labelCls}>Which game? *</label>
                <input data-testid="job-game-title" className={inputCls} value={form.game_title} onChange={(e) => setForm((f) => ({ ...f, game_title: e.target.value }))} placeholder="e.g. Rust, Garry's Mod" />
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className={labelCls}>Budget (AUD) *</label>
                <input data-testid="job-budget" required type="number" min="1" className={inputCls} value={form.budget} onChange={(e) => setForm((f) => ({ ...f, budget: e.target.value }))} placeholder="1500" />
              </div>
              <div>
                <label className={labelCls}>Skills (comma separated)</label>
                <input data-testid="job-skills" className={inputCls} value={form.skills} onChange={(e) => setForm((f) => ({ ...f, skills: e.target.value }))} placeholder="Lua, UI design, MySQL" />
              </div>
              <div>
                <label className={labelCls}>Deadline (optional)</label>
                <input data-testid="job-deadline" className={inputCls} value={form.deadline} onChange={(e) => setForm((f) => ({ ...f, deadline: e.target.value }))} placeholder="e.g. 3 weeks" />
              </div>
            </div>
            <div>
              <label className={labelCls}>Full brief *</label>
              <textarea data-testid="job-description" required minLength={20} rows={5} className={`${inputCls} resize-none`} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="What you need built, references, existing setup, framework versions — the more detail, the better the pitches." />
            </div>
            <button data-testid="job-submit" disabled={busy} className="self-start bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-black font-sans font-bold uppercase tracking-[0.2em] text-xs px-6 py-3.5 flex items-center gap-2 transition-colors">
              <Plus className="w-4 h-4" /> {busy ? "Posting…" : "Post Job"}
            </button>
          </form>
        )}

        {tab === "mine" && me && (
          <div className="flex flex-col gap-4">
            {mine.length === 0 && <p data-testid="mine-empty" className="font-mono text-[10px] text-zinc-600 py-10 text-center">You haven't posted any jobs yet.</p>}
            {mine.map((j, i) => (
              <div key={j.id} data-testid={`mine-job-${i + 1}`} className="border border-white/10 bg-black/40">
                <div className="px-5 py-4 flex items-center justify-between gap-3 flex-wrap border-b border-white/10">
                  <div className="flex items-center gap-3 flex-wrap">
                    <EngineChip engine={j.engine} gameTitle={j.game_title} />
                    <p className="font-sans font-bold uppercase text-sm text-zinc-100">{j.title}</p>
                    <span className="font-sans font-bold text-sm text-nc-green">{fmt(j.budget, 0)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`font-mono text-[8px] tracking-[0.2em] uppercase border px-2 py-1 ${j.status === "open" ? "text-nc-green border-nc-green/40 bg-nc-green/10" : j.status === "awarded" ? "text-nc-blue border-nc-blue/40 bg-nc-blue/10" : "text-zinc-500 border-white/15"}`}>{j.status}{j.status === "awarded" ? ` → ${j.awarded_name}` : ""}</span>
                    {j.status === "open" && <button data-testid={`mine-close-${i + 1}`} onClick={() => closeJob(j)} className="font-mono text-[8px] tracking-[0.2em] uppercase border border-red-400/40 text-red-400 px-2 py-1 hover:bg-red-400/10 transition-colors">Close</button>}
                    {j.status === "awarded" && j.engagement_id && <Link to={`/command?engagement=${j.engagement_id}`} data-testid={`mine-engagement-${i + 1}`} className="font-mono text-[8px] tracking-[0.2em] uppercase border border-nc-blue/40 text-nc-blue px-2 py-1 hover:bg-nc-blue/10 transition-colors">Open Engagement</Link>}
                  </div>
                </div>
                <div className="p-5 flex flex-col gap-3">
                  <p className="font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase">Pitches ({(j.applicants || []).length})</p>
                  {(j.applicants || []).length === 0 && <p className="font-mono text-[10px] text-zinc-600">No pitches yet — contractors browse new jobs daily.</p>}
                  {(j.applicants || []).map((a, ai) => (
                    <div key={a.dev_id} data-testid={`applicant-${i + 1}-${ai + 1}`} className="border border-white/10 bg-black/40 p-4 flex flex-col gap-2">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <p className="font-sans font-bold text-sm text-zinc-100 flex items-center gap-2">
                          {a.dev_name}
                          <span className="font-mono text-[8px] tracking-[0.2em] uppercase text-nc-orange border border-nc-orange/40 px-1.5 py-0.5">Clearance {a.clearance}</span>
                        </p>
                        <div className="flex items-center gap-3">
                          <span className="font-sans font-bold text-base text-nc-green">{fmt(a.rate)}</span>
                          {j.status === "open" && (
                            <button data-testid={`award-${i + 1}-${ai + 1}`} disabled={busy} onClick={() => award(j, a)} className="font-mono text-[9px] tracking-[0.2em] uppercase bg-nc-green hover:bg-nc-green/80 disabled:opacity-40 text-white px-4 py-2 transition-colors flex items-center gap-1.5">
                              <Trophy className="w-3 h-3" /> Award Job
                            </button>
                          )}
                        </div>
                      </div>
                      <p className="font-mono text-[11px] text-zinc-400 leading-relaxed">{a.pitch}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Job detail modal */}
      {detail && (
        <div data-testid="job-detail-modal" className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="max-w-2xl w-full border border-white/15 bg-[#0a0a0a] max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-white/10 px-5 py-4 flex items-center justify-between sticky top-0 bg-[#0a0a0a]">
              <div className="flex items-center gap-3"><EngineChip engine={detail.engine} gameTitle={detail.game_title} /><p className="font-sans font-bold uppercase text-base text-zinc-50">{detail.title}</p></div>
              <button onClick={() => setDetail(null)} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 flex flex-col gap-5">
              <div className="flex items-center gap-4 flex-wrap font-mono text-[10px] text-zinc-500">
                <span className="font-sans font-bold text-xl text-nc-green">{fmt(detail.budget, 0)}</span>
                {detail.deadline && <span>Deadline: {detail.deadline}</span>}
                <span>Posted by {detail.client_name} // {String(detail.created_at).slice(0, 10)}</span>
                <span>{detail.applicant_count} {detail.applicant_count === 1 ? "pitch" : "pitches"}</span>
              </div>
              <p className="font-mono text-xs text-zinc-300 leading-relaxed whitespace-pre-wrap">{detail.description}</p>
              {detail.skills.length > 0 && (
                <div className="flex flex-wrap gap-1.5">{detail.skills.map((s) => <span key={s} className="font-mono text-[9px] text-zinc-400 border border-white/10 px-2 py-1">{s}</span>)}</div>
              )}
              {canPitch ? (
                <div className="border-t border-white/10 pt-5 flex flex-col gap-4">
                  <p className="font-mono text-[10px] tracking-[0.25em] text-nc-blue uppercase flex items-center gap-2"><Send className="w-3.5 h-3.5" /> Your pitch</p>
                  <textarea data-testid="pitch-text" rows={4} className={`${inputCls} resize-none`} value={pitch.pitch} onChange={(e) => setPitch((p) => ({ ...p, pitch: e.target.value }))} placeholder="Why you're the right contractor: relevant work, approach, timeline (min 20 chars)…" />
                  <div className="flex items-center gap-3">
                    <input data-testid="pitch-rate" type="number" min="1" className={`${inputCls} max-w-[180px]`} value={pitch.rate} onChange={(e) => setPitch((p) => ({ ...p, rate: e.target.value }))} placeholder="Your rate (AUD)" />
                    <button data-testid="pitch-submit" disabled={busy || pitch.pitch.trim().length < 20 || !(parseFloat(pitch.rate) > 0)} onClick={submitPitch} className="bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white font-sans font-semibold uppercase tracking-[0.2em] text-xs px-5 py-2.5 transition-colors">Submit Pitch</button>
                  </div>
                  <p className="font-mono text-[9px] text-zinc-600 leading-relaxed">If the client awards you, an escrow engagement is created at your rate automatically.</p>
                </div>
              ) : (
                <div className="border-t border-white/10 pt-5 flex items-center gap-3 flex-wrap">
                  <p className="font-mono text-[10px] text-zinc-500 flex items-center gap-2"><Lock className="w-3.5 h-3.5" /> {me ? "Only verified contractors can pitch jobs." : "Sign in as a verified contractor to pitch this job."}</p>
                  {!me && <Link to="/auth" data-testid="pitch-login-cta" className="font-mono text-[9px] tracking-[0.2em] uppercase text-nc-blue border border-nc-blue/40 px-3 py-1.5 hover:bg-nc-blue/10 transition-colors flex items-center gap-1.5">Enlist <ArrowRight className="w-3 h-3" /></Link>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <SiteFooter />
    </div>
  );
}
